import { useState, useEffect } from 'react';
import betClient from '../../api/betClient';
import { useEffectiveTier } from '../../hooks/useEffectiveTier';
import { useAuth } from '../../contexts/AuthContext';
import LockedContentOverlay from '../upgrade/LockedContentOverlay';

const SEV_COLORS = ['','bg-[#10b981]','bg-[#34d399]','bg-[#4ade80]','bg-[#eab308]','bg-[#eab308]','bg-[#f59e0b]','bg-[#f97316]','bg-[#f97316]','bg-[#ef4444]','bg-[#dc2626]'];
const SEV_LABELS = {1:'Perfect',2:'Mild',3:'Mild',4:'Moderate',5:'Moderate',6:'Notable',7:'Significant',8:'Severe',9:'Extreme',10:'Extreme'};

function matchEmoji(c) {
  if (!c) return '\uD83C\uDF24\uFE0F';
  const l = c.toLowerCase();
  if (l.includes('snow')) return '\uD83C\uDF28\uFE0F';
  if (l.includes('thunder') || l.includes('storm')) return '\u26C8\uFE0F';
  if (l.includes('rain') || l.includes('shower') || l.includes('drizzle')) return '\uD83C\uDF27\uFE0F';
  if (l.includes('fog') || l.includes('mist')) return '\uD83C\uDF2B\uFE0F';
  if (l.includes('cloud') || l.includes('overcast')) return '\u26C5';
  return '\u2600\uFE0F';
}

export default function WeatherImpact({ eventId, sport, hasWeather, homeTeam }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(!!hasWeather);
  const [fetched, setFetched] = useState(false);
  const tier = useEffectiveTier();
  const { loading: authLoading } = useAuth();
  const [prevTier, setPrevTier] = useState(tier);
  if (tier !== prevTier) { setPrevTier(tier); setFetched(false); }

  useEffect(() => {
    if (!hasWeather || fetched || authLoading) return;
    const controller = new AbortController();
    setLoading(true);
    const params = new URLSearchParams({ sport });
    if (homeTeam) params.set('home_team', homeTeam);
    const url = `/api/ai/weather-impact/${eventId}?${params.toString()}`;
    betClient.get(url, { signal: controller.signal })
      .then((r) => setData(r.data))
      .catch(() => { if (!controller.signal.aborted) setData(null); })
      .finally(() => {
        if (!controller.signal.aborted) { setLoading(false); setFetched(true); }
      });
    return () => controller.abort();
  }, [eventId, sport, hasWeather, fetched, tier, authLoading]);

  if (!hasWeather) return null;
  if (loading) return <div className="rounded border border-[#e2e8f0] dark:border-[#1e2536] bg-white dark:bg-[#151a25] px-5 py-4"><SH>Weather Impact</SH><div className="h-28 skeleton rounded mt-3" /></div>;
  if (!data?.analysis) return null;

  const a = data.analysis;
  const w = a.weather || {};
  const score = a.severity_score;
  const bar = score >= 1 && score <= 10 ? SEV_COLORS[score] : 'bg-[#505a6e]';

  return (
    <div className="rounded border border-[#e2e8f0] dark:border-[#1e2536] bg-white dark:bg-[#151a25] px-5 py-4">
      <SH>Weather Impact</SH>

      <div className="flex items-center gap-5 flex-wrap mt-3 mb-3 rounded bg-[#f8fafc] dark:bg-[#0f1219] p-3">
        <div className="text-center"><span className="text-2xl">{matchEmoji(w.condition)}</span><p className="text-[10px] text-[#8892a8] mt-0.5">{w.condition}</p></div>
        <div className="text-center"><p className="text-xl font-bold text-[#1e293b] dark:text-[#e2e8f0]" style={{fontFamily:'var(--font-mono)'}}>{w.temp_f}&deg;F</p><p className="text-[10px] text-[#8892a8]">Temp</p></div>
        <div className="text-center"><p className="text-xl font-bold text-[#1e293b] dark:text-[#e2e8f0]" style={{fontFamily:'var(--font-mono)'}}>{w.wind_mph} <span className="text-xs font-normal text-[#8892a8]">mph</span></p><p className="text-[10px] text-[#8892a8]">Wind {w.wind_dir}</p></div>
        <div><p className="text-[11px] text-[#475569] dark:text-[#8892a8]">{w.venue}</p></div>
      </div>

      {score != null && (
        <div className="mb-3">
          <div className="flex justify-between mb-0.5"><span className="text-[10px] text-[#8892a8]">Severity</span><span className="text-[12px] font-bold text-[#1e293b] dark:text-[#e2e8f0]" style={{fontFamily:'var(--font-mono)'}}>{score}/10 <span className="text-[10px] font-normal text-[#8892a8]">({SEV_LABELS[score]||''})</span></span></div>
          <div className="h-1.5 rounded-full bg-[#e2e8f0] dark:bg-[#1a2030] overflow-hidden"><div className={`h-full rounded-full ${bar}`} style={{width:`${score*10}%`}} /></div>
        </div>
      )}

      <div className="grid grid-cols-1 sm:grid-cols-3 gap-2 mb-3">
        {a.total_impact && <Stat label="Total Impact" value={a.total_impact === 'neutral' ? 'No impact' : `Pushes ${a.total_impact} ~${a.total_impact_points} ${sport === 'baseball_mlb' ? 'runs' : 'pts'}`} cls={a.total_impact === 'neutral' ? 'text-[#10b981]' : a.total_impact === 'under' ? 'text-[#3b82f6]' : 'text-[#ef4444]'} />}
        {a.passing_impact && <Stat label="Passing" value={`${a.passing_impact} impact`} cls={a.passing_impact === 'none' || a.passing_impact === 'minimal' ? 'text-[#10b981]' : a.passing_impact === 'severe' ? 'text-[#ef4444]' : 'text-[#f59e0b]'} />}
        {a.fly_ball_impact && <Stat label="Fly Balls / HRs" value={a.fly_ball_impact} cls={a.fly_ball_impact === 'neutral' ? 'text-[#8892a8]' : a.fly_ball_impact.includes('hitter') ? 'text-[#ef4444]' : 'text-[#3b82f6]'} />}
        {a.pitching_impact && <Stat label="Pitching" value={`${a.pitching_impact} impact`} cls={a.pitching_impact === 'none' ? 'text-[#10b981]' : 'text-[#f59e0b]'} />}
        {a.bet_focus && <Stat label="Focus" value={a.bet_focus === 'none' ? 'No edge' : a.bet_focus} cls={a.bet_focus === 'none' ? 'text-[#8892a8]' : 'text-[#3b82f6]'} />}
      </div>

      {a.summary ? (
        <p className="text-[11px] text-[#475569] dark:text-[#8892a8] leading-relaxed border-t border-[#e2e8f0] dark:border-[#1e2536] pt-3">{a.summary}</p>
      ) : (
        <div className="border-t border-[#e2e8f0] dark:border-[#1e2536] pt-3">
          {a.teaser_hook && <p className="text-[11px] text-[#475569] dark:text-[#8892a8] leading-relaxed italic mb-2">{a.teaser_hook}</p>}
          <LockedContentOverlay
            description="See how the weather affects total scoring and which side has the edge."
            triggerFeature="weather_impact"
          />
        </div>
      )}
    </div>
  );
}

function SH({ children }) {
  return <div className="flex items-center gap-2"><div className="w-[3px] h-4 bg-[#3b82f6] rounded-full" /><h3 className="text-[12px] uppercase tracking-[0.05em] font-semibold text-[#64748b] dark:text-[#8892a8]" style={{fontFamily:'var(--font-sans)'}}>{children}</h3></div>;
}

function Stat({ label, value, cls }) {
  return (
    <div className="rounded bg-[#f8fafc] dark:bg-[#0f1219] p-2">
      <p className="text-[9px] text-[#8892a8] uppercase mb-0.5">{label}</p>
      <p className={`text-[11px] font-semibold capitalize ${cls}`}>{value}</p>
    </div>
  );
}
